import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useThemePalette } from '@/config/theme';
import { useTransferFeed } from '@/features/transfers/hooks';
import { formatTransferDate } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { TransferFeedEntry } from '@/lib/supabase/types';

const PHOTO = 46;

/** Player headshot with a nation-flag fallback, then initials. */
function TransferPhoto({ uri, flagUri, name }: { uri: string | null; flagUri: string | null; name: string }) {
  const [failed, setFailed] = React.useState(false);
  const showPhoto = uri && !failed;
  if (showPhoto) {
    return (
      <View className="overflow-hidden rounded-full bg-surface-2" style={{ width: PHOTO, height: PHOTO }}>
        <Image
          source={{ uri }}
          style={{ width: PHOTO, height: PHOTO }}
          contentFit="cover"
          transition={150}
          cachePolicy="memory-disk"
          onError={() => setFailed(true)}
          accessibilityLabel={name}
        />
      </View>
    );
  }
  if (flagUri) {
    return (
      <View
        className="items-center justify-center overflow-hidden rounded-full bg-surface-2"
        style={{ width: PHOTO, height: PHOTO }}
      >
        <Image
          source={{ uri: flagUri }}
          style={{ width: PHOTO, height: PHOTO }}
          contentFit="contain"
          transition={150}
          cachePolicy="memory-disk"
          accessibilityLabel={name}
        />
      </View>
    );
  }
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
  return (
    <View
      className="items-center justify-center rounded-full bg-surface-2"
      style={{ width: PHOTO, height: PHOTO }}
    >
      <AppText variant="caption" weight="bold" className="text-soft">
        {initials}
      </AppText>
    </View>
  );
}

/** A club side (crest + short name) within the from → to line. */
function ClubSide({ uri, name }: { uri: string | null; name: string | null }) {
  if (!name) {
    return (
      <View className="flex-1 flex-row items-center gap-1.5">
        <View className="h-5 w-5 rounded-full bg-surface-2" />
        <AppText variant="micro" className="text-muted" numberOfLines={1}>
          -
        </AppText>
      </View>
    );
  }
  return (
    <View className="flex-1 flex-row items-center gap-1.5">
      <TeamCrest uri={uri} name={name} size="sm" />
      <AppText variant="micro" weight="medium" className="flex-1 text-soft" numberOfLines={1}>
        {name}
      </AppText>
    </View>
  );
}

/**
 * Fee badge. API-Football's `type` is messy: a real amount ("€ 22M"), "Free",
 * "Loan", or a non-fee placeholder ("Transfer", "-", "N/A"). Only an actual
 * amount (one that contains a digit) reads as a cyan fee; the rest get a soft
 * translated chip so the row never shows raw junk.
 */
function FeeBadge({ fee }: { fee: string | null }) {
  const { t } = useTranslation();
  const raw = (fee ?? '').trim();
  const lower = raw.toLowerCase();
  let label: string;
  let tone: 'fee' | 'soft';
  if (lower === 'free' || lower === 'free transfer') {
    label = t('transfers.free');
    tone = 'soft';
  } else if (lower === 'loan') {
    label = t('transfers.loan');
    tone = 'soft';
  } else if (/\d/.test(raw)) {
    label = raw; // a genuine fee, e.g. "€ 22.5M"
    tone = 'fee';
  } else {
    label = t('transfers.undisclosed');
    tone = 'soft';
  }
  return (
    <View className={`rounded-full px-2.5 py-1 ${tone === 'fee' ? 'bg-volt-glass' : 'bg-surface-2'}`}>
      <AppText
        variant="micro"
        weight="bold"
        className={tone === 'fee' ? 'text-volt-bright' : 'text-soft'}
        numberOfLines={1}
      >
        {label}
      </AppText>
    </View>
  );
}

function TransferRow({ entry }: { entry: TransferFeedEntry }) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const name = entry.known_as ?? entry.player_name;
  const date = entry.transfer_date ? new Date(`${entry.transfer_date}T00:00:00`) : null;

  return (
    <View className="flex-row items-center gap-3 border-b border-line px-4 py-3.5">
      <TransferPhoto uri={entry.photo_url} flagUri={entry.nation_logo} name={name} />
      <View className="flex-1">
        <View className="flex-row items-center gap-2">
          <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
            {name}
          </AppText>
          <FeeBadge fee={entry.fee_label} />
        </View>

        <View className="mt-2 flex-row items-center gap-2">
          <ClubSide uri={entry.from_team_logo} name={entry.from_team_name} />
          <Ionicons name="arrow-forward" size={13} color={palette.voltBright} />
          <ClubSide uri={entry.to_team_logo} name={entry.to_team_name} />
        </View>

        {date ? (
          <AppText variant="micro" className="mt-1.5 text-muted">
            {formatTransferDate(date, numeralOptions)}
            {entry.nation_name ? `  ·  ${entry.nation_name}` : ''}
          </AppText>
        ) : null}
      </View>
    </View>
  );
}

/**
 * Transfer Center - a live mercato board sourced from API-Football: every
 * tournament player's most-recent club move with photo, from → to crests, fee
 * and date. Filled round-robin by the sync-transfers Edge Function.
 */
export default function TransfersScreen() {
  const { t } = useTranslation();
  const query = useTransferFeed(60);
  const entries = query.data ?? [];

  return (
    <Screen>
      <BackHeader title={t('transfers.title')} />

      <ScrollView contentContainerStyle={{ paddingBottom: 48 }}>
        <View className="px-4 pb-3 pt-1">
          <AppText variant="micro" className="text-muted">
            {t('transfers.subtitle')}
          </AppText>
        </View>

        {query.isPending ? (
          <View className="gap-3 px-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-16 w-full" />
            ))}
          </View>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : entries.length === 0 ? (
          <EmptyState
            icon="swap-horizontal-outline"
            title={t('transfers.emptyTitle')}
            body={t('transfers.emptyBody')}
          />
        ) : (
          <Card className="mx-4 overflow-hidden">
            {entries.map((entry) => (
              <TransferRow key={entry.player_id} entry={entry} />
            ))}
          </Card>
        )}
      </ScrollView>
    </Screen>
  );
}
